import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import log from 'loglevel';

import { useDeleteRecurrenceInboxEntry as useDeleteService } from '../../services/recurrenceInboxEntries.service';
import { useInboxEntriesStore } from '../../store';
import { useCustomToast } from '../useCustomToast';

interface UseDeleteRecurrenceInboxEntryReturn {
  deleteRecurrenceInboxEntry: (
    taskGroupId: string,
    id: string,
  ) => Promise<boolean>;
  isDeleting: boolean;
}

export function useDeleteRecurrenceInboxEntry(): UseDeleteRecurrenceInboxEntryReturn {
  const { t } = useTranslation();
  const { successMessage, errorMessage } = useCustomToast();

  const { deleteRecurrenceInboxEntry: deleteService, loading: isDeleting } =
    useDeleteService();

  const { removeRecurrenceInboxEntry } = useInboxEntriesStore(
    (state) => state.taskManager.activeTaskGroup.recurrenceEntries,
  );

  const deleteRecurrenceInboxEntry = useCallback(
    async (taskGroupId: string, id: string): Promise<boolean> => {
      try {
        const success = await deleteService(taskGroupId, id);
        if (success) {
          removeRecurrenceInboxEntry(id);
          successMessage(t('doBox.recurrences.deleteSuccess'));
        } else {
          errorMessage(t('doBox.recurrences.deleteError'));
        }
        return success;
      } catch (error) {
        log.error(error, 'Error deleting recurrence inbox entry');
        errorMessage(t('doBox.recurrences.deleteError'));
        return false;
      }
    },
    [
      deleteService,
      removeRecurrenceInboxEntry,
      successMessage,
      errorMessage,
      t,
    ],
  );

  return { deleteRecurrenceInboxEntry, isDeleting };
}
